<template>
  <a-descriptions :column="1" size="small">
    <a-descriptions-item label="Author">
      <grouped-associate-info
        :first-name="paymentInfo.creator.first_name"
        :last-name="paymentInfo.creator.last_name"
        :photo-url="paymentInfo.creator.photo_url"
        :associate-number="paymentInfo.creator.associate_number"
        :unverified="!paymentInfo.creator.is_verified"
      />
    </a-descriptions-item>
    <a-descriptions-item v-if="paymentInfo.payment && paymentInfo.payment.reviewer" label="Reviewer">
      <grouped-associate-info
        :first-name="paymentInfo.payment.reviewer?.first_name"
        :last-name="paymentInfo.payment.reviewer?.last_name"
        :photo-url="paymentInfo.payment.reviewer?.photo_url"
        :associate-number="paymentInfo.payment.reviewer?.associate_number"
        :unverified="!paymentInfo.payment.reviewer.is_verified"
      />
    </a-descriptions-item>
    <a-descriptions-item v-if="paymentInfo.payment.sender" label="Invoice From">
      <grouped-associate-info
        v-if="paymentInfo.payment.sender_type === 'associate'"
        :first-name="paymentInfo.payment.sender.first_name"
        :last-name="paymentInfo.payment.sender.last_name"
        :photo-url="paymentInfo.payment.sender.photo_url"
        :associate-number="paymentInfo.payment.sender.associate_number"
      />
      <div class="flex gap-2" v-else-if="paymentInfo.payment.sender_type === 'project'">
        <user-avatar
          :width="32"
          :height="32"
          :image-path="paymentInfo.payment.sender.logo ? paymentInfo.payment.sender.logo : undefined"
        />
        <div class="flex flex-column">
          <b class="m-0">{{ paymentInfo.payment.sender.name }}</b>
          <p>
            {{ collectAddress(paymentInfo.payment.sender.address) }}
          </p>
        </div>
      </div>
      <div v-else class="flex gap-2">
        <user-avatar :width="32" :height="32" :image-path="undefined" />
        <div class="flex flex-column">
          <b class="m-0">{{ paymentInfo.payment.sender.name }}</b>
          <p>
            {{ collectAddress(paymentInfo.payment.sender.address) }}
          </p>
        </div>
      </div>
    </a-descriptions-item>
    <a-descriptions-item v-if="paymentInfo.payment.receiver" label="Bill To">
      <div class="flex gap-2">
        <user-avatar
          :width="32"
          :height="32"
          :image-path="paymentInfo.payment.receiver.logo ? paymentInfo.payment.receiver.logo : undefined"
        />
        <div class="flex flex-column">
          <b class="m-0">{{ paymentInfo.payment.receiver.name }}</b>
          <p>
            {{ collectAddress(paymentInfo.payment.receiver.address) }}
          </p>
        </div>
      </div>
    </a-descriptions-item>
    <template v-if="paymentInfo.associate.legal_entity">
      <a-descriptions-item label="Legal Entity">
        {{ paymentInfo.associate.legal_entity?.name ?? '-' }}
      </a-descriptions-item>
      <a-descriptions-item label="Tax Id">
        {{ paymentInfo.associate.legal_entity?.tax_id ?? ' - ' }}
      </a-descriptions-item></template
    >
    <a-descriptions-item label="Number">
      {{ paymentInfo.payment.id }}
    </a-descriptions-item>
    <a-descriptions-item label="System">
      {{ combinedPaymentSystem }}
    </a-descriptions-item>
    <a-descriptions-item label="Request Type">
      {{ title }}
    </a-descriptions-item>
    <a-descriptions-item label="Status">
      <a-tag class="m-0" :color="getPaymentStatus(paymentInfo.payment.status, paymentInfo.payment.method).color">
        {{ getPaymentStatus(paymentInfo.payment.status, paymentInfo.payment.method).title }}
      </a-tag>
    </a-descriptions-item>
    <a-descriptions-item v-if="paymentInfo.payment.stripe_status" label="Stripe Status">
      <stripe-payment-status :status="paymentInfo.payment.stripe_status" />
    </a-descriptions-item>

    <template>
      <template v-if="hasAdjustedTotal"
        ><a-descriptions-item label="Original Total, $">
          {{ convertedMoney(paymentInfo.payment.fixed_total) }} </a-descriptions-item
        ><a-descriptions-item label="Current Total, $">
          {{ convertedMoney(paymentInfo.payment.total) }}
        </a-descriptions-item>
        <a-descriptions-item label="Reason">
          {{ adjustedTotalComment }}
        </a-descriptions-item>
      </template>

      <a-descriptions-item v-else label="Total, $">
        {{ convertedMoney(paymentInfo.payment.total) }}
      </a-descriptions-item>
    </template>

    <a-descriptions-item label="Subtotal, $">
      {{ convertedMoney(paymentInfo.payment.net_amount) }}
    </a-descriptions-item>
    <a-descriptions-item label="Reference Number">
      <a-input v-if="availableShiftValidation" :value="referenceNumber" @change="handleInputChange" /><span v-else>{{
        referenceNumber ?? '-'
      }}</span>
    </a-descriptions-item>
  </a-descriptions>
</template>

<script>
import GroupedAssociateInfo from '@/components/ui/grouped-associate-info.vue'
import UserAvatar from '@/components/ui/user-avatar.vue'
import { REQUEST_TYPE } from '@/store/modules/payment/constants'
import util from '@/util'
import { Descriptions, Input, Tag } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import StripePaymentStatus from '../stripe/stripe-payment-status.vue'

const { mapGetters: paymentGetters } = createNamespacedHelpers('payment')
export default {
  components: {
    'a-descriptions': Descriptions,
    'a-descriptions-item': Descriptions.Item,
    'a-tag': Tag,
    'a-input': Input,
    'grouped-associate-info': GroupedAssociateInfo,
    'user-avatar': UserAvatar,
    'stripe-payment-status': StripePaymentStatus
  },
  props: {
    paymentInfo: {
      type: Object,
      required: true
    },
    referenceNumber: {
      type: String,
      default: undefined
    },
    availableShiftValidation: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...paymentGetters(['getPaymentStatus']),

    title() {
      return this.paymentInfo.payment ? REQUEST_TYPE[this.paymentInfo.payment.type] : 'Payment'
    },

    combinedPaymentSystem() {
      let method = '-'

      if (this.paymentInfo.payment && this.paymentInfo.payment.method) {
        method = util.capitalize(this.paymentInfo.payment.method)
      }
      if (this.paymentInfo.payment.method && this.paymentInfo.payment.manual_payment_method) {
        method += ' ' + '(' + util.splitAndCapitalize(this.paymentInfo.payment.manual_payment_method) + ')'
      }

      return method
    },

    hasAdjustedTotal() {
      return !!this.paymentInfo.payment?.is_total_updated
    },

    adjustedTotalComment() {
      return this.paymentInfo.payment?.total_change_comment || '-'
    }
  },
  methods: {
    handleInputChange(e) {
      this.$emit('update:referenceNumber', e.target.value)
    },
    convertedMoney(value) {
      if (!value) {
        return 0
      }
      return util.convertDbToMoney(value)
    },
    collectAddress(address) {
      const parts = [
        address.address_line1,
        address.address_line2,
        address.city,
        address.country,
        address.state,
        address.zipcode
      ]

      const filteredParts = parts.filter(part => part)

      return filteredParts.join(', ')
    }
  }
}
</script>

<style scoped>
.adjust-comment {
  color: var(--text-color-secondary);
  font-size: 12px;
}
</style>
